import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useRecordPagination from '../../shared/useRecordPagination';
import {
  DataTable,
  RangeSlider,
  Layout,
  TextField,
  FormLayout,
  Form,
  Page,
  Card,
  Select,
  Link,
  Button,
  Tooltip,
  Text,
  BlockStack,
  Box,
  Tabs,
  Modal,
  Badge,
  Spinner,
  DropZone,
  Banner,
  List
} from '@shopify/polaris';
import CompanySelect from '../../shared/components/companySelect.jsx';
import { handleSaveWithoutLines, useUrl } from '../../shared/util/hanldesave.js';
import { formatToCurrency, getCurrenctDatetimeYYYMMDD, getRequest, postRequest, stripHtml, alphabetize, truncate, patchRequest, deleteRequest } from '../../shared/components/functions.jsx';
import { useAuthStore, useLookupStore } from '../../context/useStore.js';
import ProjectTimeLine from './components/ProjectTimeLine.jsx';
import ProjectProfitLine from './components/ProjectProfitLine.jsx';
import ProjectPiechart from './components/ProjectPieChart.jsx';
import { validateSchema } from '../../helpers/schemas/schemasHelpers.js';
import { TheSaveBar } from '../../shared/components/theSaveBar.jsx';
import Toasts from '../../shared/components/toasts/toasts.jsx';
import { getPageTitle, TabTitle } from '../../shell/helmet.jsx';
import { getSchema } from '../../shared/formSchemas.js';
import { CardHeading } from '../../shared/components/cards/CardHeading.jsx';
import DocumentCard from '../../shared/components/cards/DocumentCard.jsx';
import InvoicePdfPreviewButton from '../../shared/components/InvoicePdfPreviewButton.jsx';
import InteractionsTimeline from '../../shared/components/interactions/InteractionsTimeline.jsx';
import QuotationBuilderTab from './components/quotation-builder/QuotationBuilderTab.jsx';
import ProjectDropboxFolders from './components/ProjectDropboxFolders.jsx';

/*
    FIXME: BUSINESS LOGIC:
    - Problem with user of this component as the gebruikerID is not used but the gebruiker as a nvarchar field in the database, this is problematic
    
    - Not one invoice in is assocciated with a project so we cant do any calculations on this for outgoing invoices, ingoing invoices dont even have yet the projectID property
    - Not one outgoing invoice is coupled to a project so we cant do any calculations
    - Almost no JOBS (<15) are connected to a Project, so we can not calculate how many hours are worked on this project

    - Purchases can also be coupled but almost never done! 

    --> Simpelest way to resolve this: 
      - If on job creation this is set correctly

    
    FIXME: 
    For a correct calculation, credit notes should also be assocciated with the right project. 
    Some changes should be made on the db level for this. 
    - Add field to incoming credit note to link to project
    - Add commissie field [FLOAT] to the project table in order to know if this will be paid on commision (then the goal should be to check
    if the commision is paid on the project)

    INTERESTING: 
    Purchase can also be connected to a project, but this is not loaded yet on this page. 
*/

export default function ProjectPage() {
  let { id } = useParams();
  const pagination = useRecordPagination('projects', id, 'project');
  const navigate = useNavigate();
  const location = useLocation();
  let url = useUrl();
  const { user } = useAuthStore();
  const { projectstatuses, users, purchasetypes, overheadpercentages, setOverheadpercentages } = useLookupStore();

  const [savingResult, setSavingResult] = useState(false);
  const [initialProject, setInitialProject] = useState('');
  const [project, setProject] = useState('');
  const [invoicesOut, setInvoicesOut] = useState([]);
  const [invoicesIn, setInvoicesIn] = useState([]);
  const [creditnotesIn, setCreditnotesIn] = useState([]);
  const [associatedJobs, setAssociatedJobs] = useState([]);
  const [associatedTimeslots, setAssociatedTimeslots] = useState([]);
  const [purchaseOrderLines, setPurchaseOrderLines] = useState([]);
  const [toast, setToast] = useState(false);
  const [toastError, setToastError] = useState(false);
  const [company, setCompany] = useState(''); //eslint-disable-line
  const [showPie, setShowPie] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [supplierNames, setSupplierNames] = useState([]);
  const pageTitle = getPageTitle(project.Projectnummer, 'Project');
  const projectSchema = getSchema('project', url, null, user);
  const [oneOffModalOpen, setOneOffModalOpen] = useState(false);
  const [oneOffCost, setOneOffCost] = useState({ Bedrag: '', description: '', FactuurTypeID: '' });
  const [addingOneOff, setAddingOneOff] = useState(false);
  // Remove old PDF modal state
  // const [pdfModalOpen, setPdfModalOpen] = useState(false);
  // const [pdfModalKeys, setPdfModalKeys] = useState([]);
  // const [pdfModalSelectedKey, setPdfModalSelectedKey] = useState();
  const [budgets, setBudgets] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null); // { costTypeId, value, id }
  const [budgetDraft, setBudgetDraft] = useState('');
  const [deletingOneOffId, setDeletingOneOffId] = useState(null);
  const [financialTab, setFinancialTab] = useState('cost-items'); // Add state for financial tab
  const [expandedCostType, setExpandedCostType] = useState(null);
  const noteInputRef = useRef(null);

  // Refresh overhead percentages when component loads
  useEffect(() => {
    setOverheadpercentages();
  }, [setOverheadpercentages]);

  // Function to refresh overhead percentages (can be called manually if needed)
  const refreshOverheadPercentages = () => {
    setOverheadpercentages();
  };

  const handleChange = (value, id) => {
    if (id === 'Beslissing' && (!value || value === '')) value = null;
    if (id === 'Leverings' && (!value || value === '')) value = null;
    if (id === 'Einde' && (!value || value === '')) value = null;
    setProject(
      (project) => ({
        ...project,
        [id]: value
      }),
      setIsDirty(true)
    );
  };

  const handleDiscard = () => {
    setProject(initialProject);
    setIsDirty(false);
  };

  const handleStatusChange = (newValue) => {
    switch (newValue) {
      case '1':
        if (window.confirm('Caution: this will set the date of offerte to the date of today. Update manually if this is not true.')) {
          project.offerte = getCurrenctDatetimeYYYMMDD();
        }
        break;
      case '2':
        if (window.confirm('Caution: this will set the date of decision to the date of today. Update manually if this is not true')) {
          project.Beslissing = getCurrenctDatetimeYYYMMDD();
        }
        break;
      case '4':
        if (window.confirm('Caution: this will set the date of decision to the date of today. Update manually if this is not true.')) {
          project.Beslissing = getCurrenctDatetimeYYYMMDD();
        }
        break;
      case '5':
        if (window.confirm('Caution: this will set the date of the end of the project to the date of today. Update manually if this is not true.')) {
          project.Einde = getCurrenctDatetimeYYYMMDD();
        }
        break;
      case '6':
        if (window.confirm('Caution: this will set the date of the end of the project to the date of today. Update manually if this is not true.')) {
          project.Einde = getCurrenctDatetimeYYYMMDD();
        }
        break;
      default:
        break;
    }
  };

  async function handleSave() {
    //form validation
    const errorInSchema = await validateSchema(projectSchema, projectSchema.cast(project));

    if (!errorInSchema) {
      await handleSaveWithoutLines(id, projectSchema.cast(project), 'projects', project.ID, 'ID', setProject, setInitialProject, setIsDirty, setToastError, setToast, setSavingResult, navigate);
    } else {
      alert(errorInSchema);
    }
  }

  useEffect(() => {
    async function fetchProject() {
      //if url contains /new then set machine to its interface
      if (id === 'new') {
        setProject(projectSchema.default());
        setInitialProject(projectSchema.default());
      } else {
        //else get the machine resource via api get request
        const data = await getRequest(`/api/projects/${id}`);
        if (!data || data.length === 0) {
          setProject(projectSchema.default());
          setInitialProject(projectSchema.default());
          return;
        }
        setProject(data[0]);
        setInitialProject(data[0]);

        //fetch associated resources
        setAssociatedJobs(await getRequest(`/api/projects/${id}/jobs`));
        setInvoicesOut(await getRequest(`/api/projects/${id}/invoices/out`));
        setInvoicesIn(await getRequest(`/api/projects/${id}/invoices/in`));
        setCreditnotesIn(await getRequest(`/api/projects/${id}/creditnotes/in`));
        setAssociatedTimeslots(await getRequest(`/api/projects/${id}/jobs/timeslots`));
        setPurchaseOrderLines(await getRequest(`/api/projects/${id}/purchases/lines`));
      }
    }
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    async function fetchData() {
      if (project.firnr) {
        setCompany(await getRequest(`/api/companies/${project.firnr}`));
      }
    }
    fetchData();
  }, [project.firnr]);

  useEffect(() => {
    async function fetchSuppliers() {
      let suppliers = invoicesIn.map((i) => i.LeverancierNummer);
      let uniqueSuppliers = [...new Set(suppliers)];
      let names = await Promise.all(
        uniqueSuppliers.map(async (s) => {
          if (!s) return { id: s, name: 'One-off cost' };
          let company = await getRequest(`/api/companies/${s}`);
          return { id: s, name: company.NAAM };
        })
      );
      setSupplierNames(names);
    }
    if (invoicesIn) fetchSuppliers();
  }, [invoicesIn]);

  const totalInvoicedOut = invoicesOut.reduce((acc, invoice) => acc + Math.round(invoice.totaalbedragexclbtw * 100) / 100, 0);
  const totalInvoicedIn = invoicesIn.reduce((acc, invoice) => acc + Math.round(invoice.Bedrag * 100) / 100, 0);
  const totalCreditnotesIn = creditnotesIn.reduce((acc, creditnote) => acc + Math.round((creditnote.approval_amount || creditnote.BedragExclBtw || creditnote.Bedragfaktuur) * 100) / 100, 0);
  const totalRevenue = totalInvoicedOut + totalCreditnotesIn;
  const workingHourCostFactorEUR = 40;

  const totalWorkingHours =
    associatedTimeslots.length > 0
      ? associatedTimeslots.reduce((acc, timeslot) => {
          const startTime = new Date(timeslot.StartTechnician).toTimeString();
          const endTime = new Date(timeslot.EndTechnician).toTimeString();
          const [startHours, startMinutes] = startTime.split(':').map(Number);
          const [endHours, endMinutes] = endTime.split(':').map(Number);
          const hourDiff = endHours + endMinutes / 60 - (startHours + startMinutes / 60);
          return acc + hourDiff;
        }, 0)
      : 0;
  const totalWorkingHoursCost = totalWorkingHours * workingHourCostFactorEUR;
  const totalCost = totalInvoicedIn + totalWorkingHoursCost;

  const invoiceRows = [
    // ['Project value', formatToCurrency(project.waarde)],
    ['Invoiced out', <span style={{ color: '#00a47c', fontWeight: 500 }}>{formatToCurrency(totalInvoicedOut)}</span>],
    [
      'Credit notes in (revenue)',
      <Tooltip content="Shows approved amounts from credit note approvals">
        <span style={{ color: '#00a47c', fontWeight: 500 }}>{formatToCurrency(totalCreditnotesIn)}</span>
      </Tooltip>
    ],
    ['Total Revenue', <span style={{ color: '#00a47c', fontWeight: 600 }}>{formatToCurrency(totalRevenue)}</span>],
    ['Invoiced in', <span style={{ color: '#d82c0d', fontWeight: 500 }}>{formatToCurrency(totalInvoicedIn)}</span>],
    ['Timeslots logged by technicians', Math.round(totalWorkingHours * 100) / 100 + ' hrs'],
    ['Technicians cost (@40EUR/hr)', <span style={{ color: '#d82c0d', fontWeight: 500 }}>{formatToCurrency(totalWorkingHoursCost)}</span>],
    [<span style={{ color: '#00a47c', fontWeight: 900 }}>Marge</span>, <span style={{ color: '#00a47c', fontWeight: 600 }}>{formatToCurrency(totalRevenue - totalCost)}</span>]
  ];

  const invoiceCardOutRows = Array.isArray(invoicesOut)
    ? invoicesOut.map((item) => {
        return [
          <Link removeUnderline url={`/invoices/${item.faktuurnr}`}>
            {item.Factuurnummer}
          </Link>,
          formatToCurrency(item.totaalbedragexclbtw),
          formatToCurrency(item.totaalbedrag),
          <Tooltip content={item.memo && item.memo.length > 10 ? item.memo : ''}>
            <Text as="span">{truncate(item.memo, 15, false)}</Text>
          </Tooltip>
        ];
      })
    : [];

  // Create combined revenue items array with both invoices and credit notes
  const revenueItems = [
    // Add outgoing invoices
    ...invoicesOut.map((item) => ({
      ...item,
      type: 'invoice',
      displayType: 'Outgoing Invoice',
      number: item.Factuurnummer,
      amountExclVat: item.totaalbedragexclbtw,
      amountInclVat: item.totaalbedrag,
      note: item.memo,
      url: `/invoices/${item.faktuurnr}`
    })),
    // Add incoming credit notes (these are revenue items as they reduce costs)
    ...creditnotesIn.map((item) => ({
      ...item,
      type: 'creditnote',
      displayType: 'Incoming Credit Note',
      number: item.CreditnotaInNummer,
      amountExclVat: item.approval_amount || item.BedragExclBtw || item.Bedragfaktuur,
      amountInclVat: item.approval_amount || item.Bedragfaktuur, // Use approval amount
      note: item.approval_note || item.memo,
      url: `/creditnotes_in/${item.CreditnotaINvolgnummer}`,
      Goedgekeurd: item.Goedgekeurd,
      ApprovalID: item.ApprovalID
    }))
  ];

  const [updatingCostTypeId, setUpdatingCostTypeId] = useState(null);
  const [editingCostTypeId, setEditingCostTypeId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [updatingNoteId, setUpdatingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingAmountId, setEditingAmountId] = useState(null);
  const [updatingAmountId, setUpdatingAmountId] = useState(null);
  const [amountDraft, setAmountDraft] = useState('');
  const [bomUploadLoading, setBomUploadLoading] = useState(false);
  const [bomUploadError, setBomUploadError] = useState('');
  const [bomUploadErrorDetails, setBomUploadErrorDetails] = useState(null);
  const [bomUploadSummary, setBomUploadSummary] = useState(null);
  const [bomPurchaseType, setBomPurchaseType] = useState('');

  const uniquePurchaseTypeOptions = useMemo(() => {
    if (!Array.isArray(purchasetypes)) return [];
    const seen = new Set();
    return purchasetypes.reduce((acc, pt) => {
      const id = pt?.BestellingTypeID;
      if (!id || seen.has(id)) return acc;
      seen.add(id);
      acc.push({ value: String(id), label: pt.BestellingType || `Type ${id}` });
      return acc;
    }, []);
  }, [purchasetypes]);

  const bomPurchaseTypeOptions = useMemo(() => [{ label: 'Use default', value: '' }, ...uniquePurchaseTypeOptions], [uniquePurchaseTypeOptions]);

  const handleCostTypeChange = async (invoice, newTypeId) => {
    // Use ProjectFactuurInID for one-off costs, FactuurInID for normal
    const isOneOff = !invoice.LeverancierNummer && !invoice.FactuurInNummer;
    const id = invoice.ProjectFactuurInID;
    setUpdatingCostTypeId(id);
    try {
      // patch to update cost type
      const res = await patchRequest(`/api/invoice-approvals/${id}/cost-type`, { FactuurTypeID: newTypeId });
      setInvoicesIn((prev) =>
        prev.map((inv) => {
          const invId = inv.ProjectFactuurInID;
          return invId === id ? { ...inv, FactuurTypeID: newTypeId } : inv;
        })
      );
    } catch (e) {
      alert('Failed to update cost type');
    }
    setUpdatingCostTypeId(null);
  };

  const handleNoteEdit = (item) => {
    const isOneOff = !item.LeverancierNummer && !item.FactuurInNummer;
    let note;
    if (isOneOff) {
      note = item.description || item.Notitie || '';
    } else {
      note = item.Notitie || '';
    }
    const rowId = isOneOff ? item.ProjectFactuurInID : item.FactuurInID || item.ID;
    setEditingNoteId(rowId);
    setNoteDraft(note);
  };

  const handleNoteSave = async (item) => {
    const id = item.ProjectFactuurInID;
    setUpdatingNoteId(id);
    try {
      await patchRequest(`/api/invoice-approvals/${id}/note`, { Notitie: noteDraft });
      setInvoicesIn((prev) =>
        prev.map((inv) => {
          const invId = inv.ProjectFactuurInID;
          return invId === id ? { ...inv, Notitie: noteDraft } : inv;
        })
      );
    } catch (e) {
      alert('Failed to update note');
    }
    setUpdatingNoteId(null);
    setEditingNoteId(null);
  };

  const handleAmountEdit = (item) => {
    const rowId = item.ProjectFactuurInID;
    setEditingAmountId(rowId);
    setAmountDraft(String(item.Bedrag || ''));
  };

  const handleAmountInputChange = (value) => {
    // Allow both comma and period as decimal separators
    // Replace comma with period for internal processing
    const normalizedValue = value.replace(',', '.');
    setAmountDraft(value); // Keep original input for display
  };

  const handleAmountSave = async (item) => {
    const id = item.ProjectFactuurInID;
    setUpdatingAmountId(id);
    try {
      // Normalize the amount by replacing comma with period for parsing
      const normalizedAmount = amountDraft.replace(',', '.');
      const parsedAmount = parseFloat(normalizedAmount);

      if (isNaN(parsedAmount)) {
        alert('Please enter a valid number');
        return;
      }

      await patchRequest(`/api/invoice-approvals/${id}/amount`, { Bedrag: parsedAmount });
      setInvoicesIn((prev) =>
        prev.map((inv) => {
          const invId = inv.ProjectFactuurInID;
          return invId === id ? { ...inv, Bedrag: parsedAmount } : inv;
        })
      );
    } catch (e) {
      alert('Failed to update amount');
    }
    setUpdatingAmountId(null);
    setEditingAmountId(null);
  };

  const refreshPurchaseLines = async () => {
    if (!project.ID) return;
    const lines = await getRequest(`/api/projects/${project.ID}/purchases/lines`);
    setPurchaseOrderLines(Array.isArray(lines) ? lines : []);
  };

  const buildBomErrorDetails = (payload) => {
    if (!payload) return null;
    const details = [];
    if (payload.detectedColumns?.length) {
      details.push({
        label: 'Detected columns',
        value: payload.detectedColumns.join(', ')
      });
    }
    if (payload.sampleColumnMap) {
      const mapping = Object.entries(payload.sampleColumnMap)
        .map(([normalized, original]) => `${normalized} → ${original}`)
        .join(', ');
      details.push({
        label: 'Sample header mapping',
        value: mapping
      });
    }
    if (Array.isArray(payload.skippedRows) && payload.skippedRows.length > 0) {
      const skippedSummary = payload.skippedRows
        .slice(0, 5)
        .map((row) => `Row ${row.rowNumber}: ${row.reason}`)
        .join(' | ');
      details.push({
        label: 'Skipped rows',
        value: skippedSummary
      });
    }
    return details.length > 0 ? details : null;
  };

  const handleBomUpload = async (files, acceptedFiles) => {
    if (!project.ID || bomUploadLoading) return;
    const fileList = acceptedFiles && acceptedFiles.length > 0 ? acceptedFiles : files;
    if (!fileList || fileList.length === 0) return;
    const file = fileList[0];

    if (!file || !file.name?.toLowerCase().endsWith('.xlsx')) {
      setBomUploadError('Only Inventor BOM exports (.xlsx) are supported.');
      return;
    }

    setBomUploadLoading(true);
    setBomUploadError('');
    setBomUploadErrorDetails(null);
    setBomUploadSummary(null);

    const formData = new FormData();
    formData.append('bom', file);
    if (bomPurchaseType) formData.append('bestellingTypeId', bomPurchaseType);

    try {
      const response = await fetch(`/api/projects/${project.ID}/bom/purchase-orders`, {
        method: 'POST',
        body: formData
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        console.error('BOM upload failed', payload);
        const errorMessage = payload?.message || 'BOM upload failed. Please verify the file format.';
        const detailItems = buildBomErrorDetails(payload);
        const error = new Error(errorMessage);
        error.details = detailItems;
        throw error;
      }

      setBomUploadSummary(payload);
      setBomUploadError('');
      setBomUploadErrorDetails(null);
      await refreshPurchaseLines();
    } catch (error) {
      setBomUploadError(error.message || 'Failed to process the BOM upload.');
      setBomUploadErrorDetails(error.details || null);
      setBomUploadSummary(null);
    } finally {
      setBomUploadLoading(false);
    }
  };

  // Sort invoicesIn by cost category (FactuurTypeID) before mapping to rows
  const sortedInvoicesIn = Array.isArray(invoicesIn)
    ? [...invoicesIn].sort((a, b) => {
        // Find the cost type name for each invoice for a more user-friendly sort
        const aType = purchasetypes.find((pt) => String(pt.BestellingTypeID) === String(a.FactuurTypeID));
        const bType = purchasetypes.find((pt) => String(pt.BestellingTypeID) === String(b.FactuurTypeID));
        const aName = aType ? aType.BestellingType : '';
        const bName = bType ? bType.BestellingType : '';
        // Fallback to numeric sort if names are equal or missing
        if (aName && bName && aName !== bName) {
          return aName.localeCompare(bName);
        }
        return String(a.FactuurTypeID).localeCompare(String(b.FactuurTypeID));
      })
    : [];

  const handleDeleteOneOff = async (item) => {
    if (!window.confirm('Are you sure you want to remove this one-off cost?')) return;
    setDeletingOneOffId(item.ProjectFactuurInID);
    try {
      console.log('About to call deleteRequest');
      const result = await deleteRequest(`/api/invoice-approvals/${item.ProjectFactuurInID}`);
      console.log('deleteRequest result:', result);
      setInvoicesIn((prev) => prev.filter((inv) => inv.ProjectFactuurInID !== item.ProjectFactuurInID));
      console.log('Invoices updated');
    } catch (e) {
      console.log('Delete error:', e);
      alert('Failed to delete one-off cost');
    }
    setDeletingOneOffId(null);
  };

  const invoiceCardInRows = sortedInvoicesIn.map((item) => {
    const isOneOff = !item.LeverancierNummer && !item.FactuurInNummer;
    const costType = purchasetypes.find((pt) => String(pt.BestellingTypeID) === String(item.FactuurTypeID));
    // Always use ProjectFactuurInID for consistency since that's what we use in the API calls
    const rowId = item.ProjectFactuurInID;
    // Use InvoicePdfPreviewButton in the leftmost column
    return [
      item.S3ObjectKeys ? <InvoicePdfPreviewButton s3ObjectKeys={item.S3ObjectKeys} buttonLabel="View PDF" buttonSize="slim" buttonEmoji="👀" modalTitle="Invoice PDF Preview" /> : null,
      item.FactuurInNummer ? (
        <Link removeUnderline url={`/invoices_in/${item.faktuurinvolgnummer}`}>
          {item.FactuurInNummer}
        </Link>
      ) : (
        <span></span>
      ),
      editingAmountId === item.ProjectFactuurInID ? (
        <TextField
          value={amountDraft}
          onChange={handleAmountInputChange}
          autoFocus
          type="text"
          placeholder="0.00"
          disabled={updatingAmountId === item.ProjectFactuurInID}
          onBlur={() => handleAmountSave(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAmountSave(item);
            } else if (e.key === 'Escape') {
              setEditingAmountId(null);
            }
          }}
        />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ cursor: 'pointer' }} onClick={() => handleAmountEdit(item)}>
            {formatToCurrency(item.Bedrag)}
          </span>
          {item.LandID === 300 && (
            <Badge tone="attention" size="small">
              🇧🇪 VAT ?
            </Badge>
          )}
        </div>
      ),
      item.LeverancierNummer ? (
        <Link removeUnderline url={`/companies/${item.LeverancierNummer}`}>
          {supplierNames.length > 0 && supplierNames.filter((i) => i.id === item.LeverancierNummer)[0]?.name}
        </Link>
      ) : (
        <span>-</span>
      ),
      editingCostTypeId === item.ProjectFactuurInID ? (
        <Select
          options={uniquePurchaseTypeOptions}
          value={item.FactuurTypeID != null ? String(item.FactuurTypeID) : uniquePurchaseTypeOptions[0]?.value || ''}
          onChange={async (val) => {
            await handleCostTypeChange(item, val);
            setEditingCostTypeId(null);
          }}
          onBlur={() => setEditingCostTypeId(null)}
          autoFocus
          disabled={updatingCostTypeId === item.ProjectFactuurInID}
        />
      ) : (
        <span style={{ cursor: 'pointer' }} onClick={() => setEditingCostTypeId(item.ProjectFactuurInID)}>
          <Badge>{costType ? costType.BestellingType : 'Unknown'}</Badge>
        </span>
      ),
      // Invoice text column
      item.memo ? (
        <Tooltip content={item.memo && item.memo.length > 10 ? item.memo : ''}>
          <Text as="span">{truncate(item.memo, 15, false)}</Text>
        </Tooltip>
      ) : null,
      // Note column
      editingNoteId === item.ProjectFactuurInID ? (
        <TextField
          value={noteDraft}
          onChange={setNoteDraft}
          autoFocus
          multiline={2}
          disabled={updatingNoteId === item.ProjectFactuurInID}
          onBlur={() => handleNoteSave(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleNoteSave(item);
            }
          }}
          ref={noteInputRef}
        />
      ) : (
        <Box display="flex" gap="100">
          <span style={{ cursor: 'pointer' }} onClick={() => handleNoteEdit(item)}>
            {(() => {
              const note = isOneOff ? (item.description ? item.description : item.Notitie) : item.Notitie;
              if (note && note.length > 60) {
                return (
                  <Tooltip content={note}>
                    <Text as="span">{note}</Text>
                  </Tooltip>
                );
              } else if (note) {
                return <Text as="span">{note}</Text>;
              } else {
                return (
                  <Text as="span" color="subdued">
                    (add note)
                  </Text>
                );
              }
            })()}
          </span>
          {isOneOff && (
            <Button size="slim" tone="critical" loading={deletingOneOffId === item.ProjectFactuurInID} onClick={() => handleDeleteOneOff(item)} disabled={deletingOneOffId === item.ProjectFactuurInID}>
              Remove
            </Button>
          )}
        </Box>
      )
    ];
  });

  // Fetch budgets for the current project
  useEffect(() => {
    async function fetchBudgets() {
      if (!project.ID) return;
      setLoadingBudgets(true);
      try {
        const res = await getRequest(`/api/project_budgetten?ProjectID=${project.ID}`);
        setBudgets(res || []);
      } catch (e) {
        setBudgets([]);
      }
      setLoadingBudgets(false);
    }
    if (project.ID) fetchBudgets();
  }, [project.ID]);

  // Helper: get budget object for a cost type
  const getBudgetForType = (costTypeId) => budgets.find((b) => String(b.CostTypeID) === String(costTypeId));

  // Add or edit budget handlers
  const handleBudgetClick = (costTypeId) => {
    const budgetObj = getBudgetForType(costTypeId);
    setEditingBudget({
      costTypeId,
      value: budgetObj ? budgetObj.Budget : '',
      id: budgetObj ? budgetObj.ID : null
    });
    setBudgetDraft(budgetObj ? String(budgetObj.Budget) : '');
  };
  const handleBudgetInputChange = (val) => setBudgetDraft(val);
  const handleBudgetInputBlur = async () => {
    if (!editingBudget) return;
    const { costTypeId, id } = editingBudget;
    if (budgetDraft === '' || isNaN(Number(budgetDraft))) {
      setEditingBudget(null);
      return;
    }
    setLoadingBudgets(true);
    try {
      if (id) {
        // Edit existing
        await patchRequest(`/api/project_budgetten/${id}/budget`, { Budget: parseFloat(budgetDraft) });
      } else {
        // Add new
        await postRequest('/api/project_budgetten', { ProjectID: project.ID, CostTypeID: costTypeId, Budget: parseFloat(budgetDraft) });
      }
      // Refresh
      const res = await getRequest(`/api/project_budgetten?ProjectID=${project.ID}`);
      setBudgets(res || []);
    } catch (e) {}
    setEditingBudget(null);
    setLoadingBudgets(false);
  };
  const handleBudgetInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBudgetInputBlur();
    } else if (e.key === 'Escape') {
      setEditingBudget(null);
    }
  };

  // Handle clicking on a cost type to toggle supplier breakdown
  const handleCostTypeClick = (costTypeId) => {
    setExpandedCostType(expandedCostType === costTypeId ? null : costTypeId);
  };

  // Get supplier breakdown data for a cost type
  const getSupplierBreakdown = (costTypeId) => {
    const costTypeInvoices = invoicesIn.filter((inv) => String(inv.FactuurTypeID) === String(costTypeId));

    // Group by supplier
    const supplierGroups = {};
    costTypeInvoices.forEach((invoice) => {
      const supplierId = invoice.LeverancierNummer || 'one-off';
      const supplierName = supplierId === 'one-off' ? 'One-off costs' : supplierNames.find((s) => s.id === supplierId)?.name || 'Unknown Supplier';

      if (!supplierGroups[supplierId]) {
        supplierGroups[supplierId] = {
          id: supplierId,
          name: supplierName,
          total: 0,
          count: 0,
          invoices: []
        };
      }

      supplierGroups[supplierId].total += parseFloat(invoice.Bedrag) || 0;
      supplierGroups[supplierId].count += 1;
      supplierGroups[supplierId].invoices.push(invoice);
    });

    // Convert to array and sort by total
    return Object.values(supplierGroups).sort((a, b) => b.total - a.total);
  };

  // --- Financial Tab Content ---
  // --- Metrics for cost categories ---
  // Aggregate costs per cost-category (FactuurTypeID)
  const costCategoryStats = purchasetypes
    .map((pt) => {
      const total = invoicesIn.filter((inv) => String(inv.FactuurTypeID) === String(pt.BestellingTypeID)).reduce((acc, inv) => acc + (parseFloat(inv.Bedrag) || 0), 0);
      const budgetObj = getBudgetForType(pt.BestellingTypeID);
      const budget = budgetObj ? parseFloat(budgetObj.Budget) : null;
      const diffPercent = budget != null && budget !== 0 ? ((total - budget) / budget) * 100 : null;
      return {
        id: pt.BestellingTypeID,
        name: pt.BestellingType,
        total,
        budget,
        diffPercent
      };
    })
    // Only include cost types that are present in the cost items table (invoicesIn)
    .filter((cat) => invoicesIn.some((inv) => String(inv.FactuurTypeID) === String(cat.id)));

  const totalCosts = costCategoryStats.reduce((acc, cat) => acc + cat.total, 0);
  const brutoMargin = totalRevenue - totalCosts;
  const brutoMarginPercent = totalRevenue > 0 ? (brutoMargin / totalRevenue) * 100 : 0;

  // Calculate overhead percentage for project year (use delivery date, fallback to current year)
  // This ensures that projects from different years use the appropriate overhead percentage
  const projectYear = project.Leverings ? new Date(project.Leverings).getFullYear() : new Date().getFullYear();
  const overheadPercentage = overheadpercentages?.find((op) => op.Year === projectYear)?.OverheadPercentage || 11.3; // Default to 11.3% for 2025

  // Calculate overhead amount based on total revenue
  // This makes sense because overhead is a company-wide cost that should be allocated based on actual revenue generated
  const overheadAmount = totalRevenue * (overheadPercentage / 100);

  // Show warning if no overhead percentage is found for the project year
  const overheadPercentageFound = overheadpercentages.some((op) => op.Year === projectYear);
  const netMargin = brutoMargin - overheadAmount;
  const netMarginPercent = totalRevenue > 0 ? (netMargin / totalRevenue) * 100 : 0;

  // Check if margins meet targets
  const grossMarginTarget = 25; // >25%
  const netMarginTarget = 10; // >10%
  const grossMarginMeetsTarget = brutoMarginPercent >= grossMarginTarget;
  const netMarginMeetsTarget = netMarginPercent >= netMarginTarget;

  // Helper function to get margin color based on target status
  const getMarginColor = (meetsTarget) => (meetsTarget ? '#00a47c' : '#ff6b35');

  // --- Cost Breakdown Table Rows ---
  const costCategoryRows = [];

  // Total Revenue (invoices out + credit notes in)
  costCategoryRows.push([<span style={{ fontWeight: 600 }}>Total Revenue</span>, '', <span style={{ color: '#00a47c', fontWeight: 600 }}>+{formatToCurrency(totalRevenue)}</span>, '']);

  // Each cost category (cost, negative)
  costCategoryStats.forEach((cat) => {
    const costPercent = totalCosts > 0 ? ((cat.total / totalCosts) * 100).toFixed(1) : null;
    const diffValue = cat.budget != null && cat.total != null ? cat.total - cat.budget : null;
    const diffPercent = cat.budget != null && cat.total != null && cat.budget !== 0 ? ((cat.total - cat.budget) / cat.budget) * 100 : null;
    const isExpanded = expandedCostType === cat.id;
    const supplierBreakdown = getSupplierBreakdown(cat.id);

    // Main cost category row
    costCategoryRows.push([
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ cursor: 'pointer', color: '#005fa3', textDecoration: 'underline', fontWeight: 500 }} onClick={() => handleCostTypeClick(cat.id)} title="Click to expand supplier breakdown">
          {cat.name} {isExpanded ? '▼' : '▶'}
        </span>
      </div>,
      loadingBudgets ? (
        <Spinner size="small" />
      ) : editingBudget && editingBudget.costTypeId === cat.id ? (
        <TextField value={budgetDraft} onChange={handleBudgetInputChange} onBlur={handleBudgetInputBlur} onKeyDown={handleBudgetInputKeyDown} autoFocus type="number" min={0} style={{ width: 80 }} />
      ) : cat.budget != null ? (
        <span style={{ cursor: 'pointer', color: '#005fa3', textDecoration: 'underline' }} onClick={() => handleBudgetClick(cat.id)}>
          {formatToCurrency(cat.budget)}
        </span>
      ) : (
        <Button size="slim" onClick={() => handleBudgetClick(cat.id)}>
          Add budget
        </Button>
      ),
      <span style={{ color: '#d82c0d' }}>
        - {formatToCurrency(cat.total)}
        {costPercent !== null ? <span style={{ color: '#888', fontWeight: 400 }}> ({costPercent}%)</span> : ''}
      </span>,
      diffValue !== null && diffPercent !== null ? (
        <span style={{ color: diffValue > 0 ? '#d82c0d' : '#00a47c', fontWeight: 500 }}>
          {diffValue > 0 ? '+' : ''}
          {formatToCurrency(diffValue)}
          {diffPercent !== null ? ` (${diffPercent > 0 ? '+' : ''}${diffPercent.toFixed(1)}%)` : ''}
        </span>
      ) : (
        ''
      )
    ]);

    // Expanded supplier breakdown rows
    if (isExpanded && supplierBreakdown.length > 0) {
      supplierBreakdown.forEach((supplier, index) => {
        costCategoryRows.push([
          <div style={{ paddingLeft: '24px', fontSize: '14px', color: '#666' }}>
            <span style={{ fontWeight: 500 }}>{supplier.name}</span>
            <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>
              ({supplier.count} invoice{supplier.count !== 1 ? 's' : ''})
            </span>
          </div>,
          '', // No budget for individual suppliers
          <span style={{ color: '#d82c0d', fontSize: '14px' }}>- {formatToCurrency(supplier.total)}</span>,
          <div style={{ fontSize: '12px', color: '#666', maxWidth: '200px' }}>
            {supplier.invoices.slice(0, 3).map((inv, idx) => (
              <div key={idx} style={{ marginBottom: '2px' }}>
                {inv.FactuurInNummer || 'One-off'} - {formatToCurrency(inv.Bedrag)}
                {inv.Notitie && <span style={{ color: '#888', fontStyle: 'italic' }}> ({inv.Notitie})</span>}
              </div>
            ))}
            {supplier.invoices.length > 3 && <div style={{ color: '#888', fontStyle: 'italic' }}>+{supplier.invoices.length - 3} more...</div>}
          </div>
        ]);
      });
    }
  });

  // Total Costs (sum of all cost categories)
  costCategoryRows.push([<span style={{ fontWeight: 600 }}>Total Costs</span>, '', <span style={{ color: '#d82c0d', fontWeight: 600 }}>- {formatToCurrency(totalCosts)}</span>, '']);

  // Gross Margin (income - costs)
  costCategoryRows.push([
    <span style={{ fontWeight: 700, color: grossMarginMeetsTarget ? '#00a47c' : '#ff6b35' }}>
      Gross Margin {grossMarginMeetsTarget ? '✅' : '⚠️'} (Target: &gt;{grossMarginTarget}%)
    </span>,
    '',
    <span style={{ fontWeight: 700, color: getMarginColor(grossMarginMeetsTarget) }}>
      {formatToCurrency(brutoMargin)} <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>({brutoMarginPercent.toFixed(1)}%)</span>
    </span>,
    ''
  ]);

  // Overhead (calculated on total revenue)
  costCategoryRows.push([
    <span style={{ fontWeight: 600, color: '#d82c0d' }}>
      {project.Leverings ? (
        `Overhead (${overheadPercentage}% of revenue)`
      ) : (
        <span style={{ color: '#d82c0d' }}>
          Overhead <span style={{ color: '#ff6b35', fontWeight: 700 }}>(Delivery date not set!)</span>
        </span>
      )}
      <br />
      <span style={{ fontSize: '12px', color: '#666', fontWeight: 400 }}>
        {project.Leverings ? (
          <>
            Year {projectYear}
            {!overheadPercentageFound && <span style={{ color: '#ff6b35', fontWeight: 500 }}> • ⚠️ No overhead % set for {projectYear}, using default</span>}•{' '}
            <button
              onClick={refreshOverheadPercentages}
              style={{
                background: 'none',
                border: 'none',
                color: '#005fa3',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Refresh
            </button>
          </>
        ) : (
          <span style={{ color: '#d82c0d', fontWeight: 500 }}>⚠️ Please set the delivery date in Project Details to calculate overhead percentage</span>
        )}
      </span>
    </span>,
    '',
    <span style={{ fontWeight: 600, color: '#d82c0d' }}>
      - {formatToCurrency(overheadAmount)}
      <br />
      <span style={{ fontSize: '12px', color: '#666', fontWeight: 400 }}>Based on revenue: {formatToCurrency(totalRevenue)}</span>
    </span>,
    ''
  ]);

  // Net Margin (gross margin - overhead)
  costCategoryRows.push([
    <span style={{ fontWeight: 700, color: netMarginMeetsTarget ? '#00a47c' : '#ff6b35' }}>
      Net Margin {netMarginMeetsTarget ? '✅' : '⚠️'} (Target: &gt;{netMarginTarget}%)
    </span>,
    '',
    <span style={{ fontWeight: 700, color: getMarginColor(netMarginMeetsTarget) }}>
      {formatToCurrency(netMargin)} <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>({netMarginPercent.toFixed(1)}%)</span>
    </span>,
    ''
  ]);

  // --- Financial Tab Content ---
  const FinancialTab = (
    <Box width="100%">
      <BlockStack gap="400">
        {/* Cost Breakdown and Melding - responsive layout */}
        <Box maxWidth="" margin="auto">
          {expandedCostType ? (
            // Full width layout when expanded
            <Layout>
              {/* Cost Breakdown Card - Full Width */}
              <Layout.Section>
                <Card>
                  <CardHeading title="Cost Breakdown" />
                  <div style={{ marginBottom: '8px' }}>
                    <Badge tone="info" size="small">
                      📊 Expanded view - Click any cost type to collapse
                    </Badge>
                  </div>
                  <DataTable
                    className="cost-breakdown-table-expanded"
                    increasedTableDensity={true}
                    verticalAlign="middle"
                    columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                    headings={['Cost Category', 'Budget', 'Aggregated total', 'Budgetted difference']}
                    rows={costCategoryRows.length > 0 ? costCategoryRows : [['No data', '-', '-', '-']]}
                  />
                  <style>{`
                    .cost-breakdown-table-expanded table {
                      table-layout: fixed;
                    }
                    .cost-breakdown-table-expanded th:nth-child(1),
                    .cost-breakdown-table-expanded td:nth-child(1) {
                      width: 30% !important;
                      max-width: 250px !important;
                      word-wrap: break-word;
                      overflow-wrap: break-word;
                    }
                    .cost-breakdown-table-expanded th:nth-child(2),
                    .cost-breakdown-table-expanded td:nth-child(2) {
                      width: 15% !important;
                      max-width: 100px !important;
                    }
                    .cost-breakdown-table-expanded th:nth-child(3),
                    .cost-breakdown-table-expanded td:nth-child(3) {
                      width: 20% !important;
                      max-width: 120px !important;
                    }
                    .cost-breakdown-table-expanded th:nth-child(4),
                    .cost-breakdown-table-expanded td:nth-child(4) {
                      width: 35% !important;
                      min-width: 200px !important;
                    }
                  `}</style>
                </Card>
              </Layout.Section>
              {/* Melding textarea Card - Below */}
              <Layout.Section>
                <Card>
                  <CardHeading title="Status & Lessons Learned" />
                  <TextField
                    id="Melding"
                    value={project.Melding || ''}
                    onChange={(val) => handleChange(val, 'Melding')}
                    multiline={8}
                    placeholder="Type status updates, lessons learned, or notes regarding the cost review of this project..."
                    autoComplete="off"
                    disabled={savingResult}
                  />
                </Card>
              </Layout.Section>
            </Layout>
          ) : (
            // Side by side layout when not expanded
            <Layout>
              {/* Cost Breakdown Card */}
              <Layout.Section variant="oneHalf">
                <Card>
                  <CardHeading title="Cost Breakdown" />
                  <DataTable
                    increasedTableDensity={true}
                    verticalAlign="middle"
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                    headings={['Cost Category', 'Budget', 'Aggregated total', 'Budgetted difference']}
                    rows={costCategoryRows.length > 0 ? costCategoryRows : [['No data', '-', '-', '-']]}
                  />
                </Card>
              </Layout.Section>
              {/* Melding textarea Card */}
              <Layout.Section variant="">
                <Card>
                  <CardHeading title="Status & Lessons Learned" />
                  <TextField
                    id="Melding"
                    value={project.Melding || ''}
                    onChange={(val) => handleChange(val, 'Melding')}
                    multiline={12}
                    placeholder="Type status updates, lessons learned, or notes regarding the cost review of this project..."
                    autoComplete="off"
                    disabled={savingResult}
                  />
                </Card>
              </Layout.Section>
            </Layout>
          )}
        </Box>
        {/* Profit & Analytics at the top */}
        <div>
          <ProjectProfitLine project={project} totalInvoicedOut={totalInvoicedOut} />
        </div>

        {/* Financial Tabs */}
        <div>
          {/* Financial Tabs */}
          <Tabs
            tabs={[
              {
                id: 'cost-items',
                content: 'Cost Items',
                panelID: 'cost-items-content'
              },
              {
                id: 'revenue-items',
                content: 'Revenue Items',
                panelID: 'revenue-items-content'
              },
              {
                id: 'technician-costs',
                content: 'Technician Costs',
                panelID: 'technician-costs-content'
              },
              {
                id: 'purchase-orders',
                content: 'Purchased Items',
                panelID: 'purchase-orders-content'
              }
            ]}
            selected={financialTab === 'cost-items' ? 0 : financialTab === 'revenue-items' ? 1 : financialTab === 'technician-costs' ? 2 : 3}
            onSelect={(selectedTabIndex) => {
              const tabMap = ['cost-items', 'revenue-items', 'technician-costs', 'purchase-orders'];
              setFinancialTab(tabMap[selectedTabIndex]);
            }}
          >
            <div id="cost-items-content" role="tabpanel" aria-labelledby="cost-items">
              {financialTab === 'cost-items' && (
                <>
                  <Box paddingBlockEnd="200">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingBottom: 'var(--p-space-200)' }}>
                      <Button onClick={() => setOneOffModalOpen(true)} variant="primary">
                        Add cost
                      </Button>
                    </div>
                  </Box>
                  {/* Invoices In Table */}
                  <BlockStack gap="200">
                    <div>
                      <strong>Cost items</strong>
                      {invoicesIn.length > 0 ? (
                        <>
                          <style>{`
                            .project-invoices-in-table td:nth-child(4),
                            .project-invoices-in-table th:nth-child(4) {
                              width: 75%;
                              max-width: 75%;
                              min-width: 300px;
                              white-space: normal;
                              word-break: break-word;
                            }
                            .project-invoices-in-table td:not(:nth-child(4)),
                            .project-invoices-in-table th:not(:nth-child(4)) {
                              width: 6.25%;
                              max-width: 6.25%;
                              min-width: 40px;
                            }
                            .project-invoices-in-table td:nth-child(7),
                            .project-invoices-in-table th:nth-child(7) {
                              width: 30%;
                              max-width: 30%;
                              min-width: 200px;
                              white-space: normal;
                              word-break: break-word;
                            }
                          `}</style>
                          <DataTable
                            className="project-invoices-in-table"
                            stickyHeader={true}
                            increasedTableDensity={true}
                            verticalAlign="middle"
                            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                            headings={['', 'Number', 'Value', 'Supplier', 'Cost type', 'Invoice text', 'Cost Note']}
                            rows={[
                              ...invoiceCardInRows,
                              ['', <span style={{ fontWeight: 700 }}>Total</span>, <span style={{ fontWeight: 700 }}>{formatToCurrency(totalInvoicedIn)}</span>, '', '', '', '']
                            ]}
                          />
                        </>
                      ) : (
                        'No incoming invoices for this project'
                      )}
                    </div>
                  </BlockStack>
                </>
              )}
            </div>

            <div id="revenue-items-content" role="tabpanel" aria-labelledby="revenue-items">
              {financialTab === 'revenue-items' && (
                <div style={{ paddingTop: '44px' }}>
                  <strong>Revenue items</strong>
                  {revenueItems.length > 0 ? (
                    <DataTable
                      stickyHeader={true}
                      increasedTableDensity={true}
                      verticalAlign="middle"
                      columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                      headings={['Type', 'Number', 'Value (excl. VAT)', 'Value (incl. VAT)', 'Note']}
                      rows={[
                        ...revenueItems.map((item) => [
                          <div>
                            <Badge tone={item.type === 'invoice' ? 'success' : 'info'}>{item.displayType}</Badge>
                            {item.type === 'creditnote' && <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{item.Goedgekeurd ? '✓ Approved' : '⏳ Pending'}</div>}
                          </div>,
                          <Link removeUnderline url={item.url}>
                            {item.number}
                          </Link>,
                          formatToCurrency(item.amountExclVat),
                          formatToCurrency(item.amountInclVat),
                          <Tooltip content={item.note && item.note.length > 10 ? item.note : ''}>
                            <Text as="span">{truncate(item.note, 15, false)}</Text>
                          </Tooltip>
                        ]),
                        [
                          <span style={{ fontWeight: 700 }}>Total Revenue</span>,
                          '',
                          <span style={{ fontWeight: 700 }}>{formatToCurrency(totalRevenue)}</span>,
                          <span style={{ fontWeight: 700 }}>{formatToCurrency(totalRevenue * 1.21)}</span>,
                          ''
                        ]
                      ]}
                    />
                  ) : (
                    'No revenue items for this project'
                  )}
                </div>
              )}
            </div>

            <div id="technician-costs-content" role="tabpanel" aria-labelledby="technician-costs">
              {financialTab === 'technician-costs' && (
                <div style={{ paddingTop: '44px' }}>
                  <strong>Technician Costs</strong>
                  <DataTable
                    increasedTableDensity={true}
                    verticalAlign="middle"
                    columnContentTypes={['text', 'numeric']}
                    headings={['Timeslots logged', 'Technicians cost (@40EUR/hr)']}
                    rows={[[Math.round(totalWorkingHours * 100) / 100 + ' hrs', <span style={{ fontWeight: 700 }}>{formatToCurrency(totalWorkingHoursCost)}</span>]]}
                  />

                  <Box paddingBlockStart="400">
                    <strong>Total Costs</strong>
                    <DataTable
                      increasedTableDensity={true}
                      verticalAlign="middle"
                      columnContentTypes={['text', 'numeric']}
                      headings={['Total Costs', '']}
                      rows={[[<span style={{ fontWeight: 700 }}>Total Costs</span>, <span style={{ fontWeight: 700 }}>{formatToCurrency(totalCost)}</span>]]}
                    />
                  </Box>
                </div>
              )}
            </div>

            <div id="purchase-orders-content" role="tabpanel" aria-labelledby="purchase-orders">
              {financialTab === 'purchase-orders' && (
                <div style={{ paddingTop: '44px' }}>
                  <BlockStack gap="400">
                    {project.ID && (
                      <Card>
                        <BlockStack gap="200">
                          <div>
                            <CardHeading title="Generate purchase orders from BOM" />
                            <Text as="p" variant="bodySm">
                              Drop the Inventor BOM export (.xlsx). We will group lines per supplier, create the purchase orders automatically, and link them to this project.
                            </Text>
                          </div>
                          <FormLayout>
                            <Select
                              label="Purchase type (optional)"
                              placeholder="Keep purchase type empty"
                              options={bomPurchaseTypeOptions}
                              value={bomPurchaseType}
                              onChange={(value) => setBomPurchaseType(value)}
                              disabled={bomUploadLoading}
                            />
                            <DropZone allowMultiple={false} accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onDrop={handleBomUpload} disabled={bomUploadLoading}>
                              <DropZone.FileUpload actionHint="Upload Inventor BOM (.xlsx)" />
                            </DropZone>
                            {bomUploadLoading && (
                              <Box paddingBlockStart="100">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p-space-150)' }}>
                                  <Spinner size="small" />
                                  <Text variant="bodySm" as="p">
                                    Generating purchase orders…
                                  </Text>
                                </div>
                              </Box>
                            )}
                            {bomUploadError && (
                              <Banner tone="critical" title="BOM upload failed">
                                <p>{bomUploadError}</p>
                                {bomUploadErrorDetails && (
                                  <Box paddingBlockStart="100">
                                    <List type="bullet">
                                      {bomUploadErrorDetails.map((detail) => (
                                        <List.Item key={detail.label}>
                                          <strong>{detail.label}:</strong> {detail.value}
                                        </List.Item>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </Banner>
                            )}
                            {bomUploadSummary && (
                              <div>
                                <Banner tone="success" title="Purchase orders created">
                                  <p>
                                    {bomUploadSummary.totalSuppliers} suppliers · {bomUploadSummary.totalLines} BOM lines · {bomUploadSummary.unknownProducts} unknown products (assigned to generic
                                    code #1)
                                  </p>
                                </Banner>
                                <Box paddingBlockStart="200">
                                  <List type="bullet">
                                    {bomUploadSummary.createdOrders.map((order) => (
                                      <List.Item key={order.purchaseId}>
                                        <Link removeUnderline url={`/purchases/${order.purchaseId}`}>
                                          PO {order.purchaseNumber}
                                        </Link>{' '}
                                        — {order.supplierName || order.supplierId} — {order.lineCount} lines — {formatToCurrency(order.lineTotal || 0)}
                                      </List.Item>
                                    ))}
                                  </List>
                                </Box>
                                {Array.isArray(bomUploadSummary.skippedRows) && bomUploadSummary.skippedRows.length > 0 && (
                                  <Box paddingBlockStart="200">
                                    <Text as="p" tone="subdued" variant="bodySm">
                                      Skipped rows (Excel row numbers): {bomUploadSummary.skippedRows.map((row) => row.rowNumber).join(', ')}
                                    </Text>
                                  </Box>
                                )}
                              </div>
                            )}
                          </FormLayout>
                        </BlockStack>
                      </Card>
                    )}
                    <div>
                      <strong>Purchase Order Lines</strong>
                      {purchaseOrderLines.length > 0 ? (
                        <DataTable
                          stickyHeader={true}
                          increasedTableDensity={true}
                          verticalAlign="middle"
                          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'numeric', 'text']}
                          headings={['Order Number', 'Date', 'Supplier', 'Customer', 'Product', 'Description', 'Quantity', 'Unit Price', 'Status']}
                          rows={purchaseOrderLines.map((item) => [
                            <Link removeUnderline url={`/purchases/${item.BestellingID}`}>
                              {item.Bestellingnummer}
                            </Link>,
                            String(item.Datum).substring(0, 10),
                            item.supplier_name ? (
                              <Link removeUnderline url={`/companies/${item.supplier_number}`}>
                                {item.supplier_name}
                              </Link>
                            ) : (
                              '-'
                            ),
                            item.customer_name ? (
                              <Link removeUnderline url={`/companies/${item.customer_number}`}>
                                {item.customer_name}
                              </Link>
                            ) : (
                              '-'
                            ),
                            item.produktnr ? (
                              <Link removeUnderline url={`/products/${item.produktnr}`}>
                                #{item.produktnr}
                              </Link>
                            ) : (
                              '-'
                            ),
                            item.tekstlijn || '-',
                            item.aantal || 0,
                            formatToCurrency(item.VP || 0),
                            <div>
                              {(() => {
                                const statusId = item.PurchaseStatusID || (item.Geannuleerd ? 5 : item.Ontvangen ? 4 : item.Verstuurd ? 2 : 1);
                                const statusLabel =
                                  item.purchase_status_name || (statusId === 5 ? 'Cancelled' : statusId === 4 ? 'Received' : statusId === 3 ? 'Partially received' : statusId === 2 ? 'Sent' : 'Draft');
                                const tone = statusId === 5 ? 'critical' : statusId === 4 ? 'success' : statusId === 3 ? 'attention' : statusId === 2 ? 'info' : undefined;
                                return <Badge tone={tone}>{statusLabel}</Badge>;
                              })()}
                            </div>
                          ])}
                        />
                      ) : (
                        'No purchase order lines for this project'
                      )}
                    </div>
                  </BlockStack>
                </div>
              )}
            </div>
          </Tabs>
        </div>

        {/* One-off cost modal */}
        <Modal
          open={oneOffModalOpen}
          onClose={() => setOneOffModalOpen(false)}
          title="Add Cost"
          primaryAction={{
            content: addingOneOff ? 'Adding...' : 'Add',
            onAction: handleAddOneOffCost,
            loading: addingOneOff,
            disabled: addingOneOff || !oneOffCost.Bedrag || !oneOffCost.FactuurTypeID || !project.ID
          }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setOneOffModalOpen(false) }]}
        >
          <Modal.Section>
            <FormLayout>
              <TextField label="Amount (€)" type="number" value={oneOffCost.Bedrag} onChange={(val) => setOneOffCost((prev) => ({ ...prev, Bedrag: val }))} autoComplete="off" min={0} required />
              <TextField label="Description" value={oneOffCost.description} onChange={(val) => setOneOffCost((prev) => ({ ...prev, description: val }))} autoComplete="off" multiline={3} />
              <Select
                label="Cost type"
                placeholder="Select cost type"
                options={uniquePurchaseTypeOptions}
                value={oneOffCost.FactuurTypeID}
                onChange={(val) => setOneOffCost((prev) => ({ ...prev, FactuurTypeID: val }))}
                required
              />
            </FormLayout>
          </Modal.Section>
        </Modal>
      </BlockStack>
    </Box>
  );

  // --- Tabs Setup ---
  const tabs = [
    {
      id: 'project-details',
      content: 'Project details',
      panelID: 'project-details-content',
      comp: (
        <>
          <div style={{ marginBottom: 24 }}>
            {/* <Banner title="Project summary">
              <ProjectTimeLine project={project} />
            </Banner> */}
          </div>
          <Layout>
            <Layout.Section variant="oneHalf">
              <Card>
                <Form onSubmit={() => handleSave()} autoComplete="off">
                  <FormLayout>
                    <CompanySelect isClearable={false} isDisabled={savingResult} id="firnr" label="Customer" value={project.firnr} onChange={handleChange} />
                    <FormLayout.Group>
                      <TextField id="Projectnummer" disabled label="Number" value={project.Projectnummer ? String(project.Projectnummer) : 'created automatically'} />
                      <TextField id="waarde" type="number" onChange={handleChange} label="Project value (excl. VAT)" prefix="€" value={project.waarde} disabled={savingResult} />
                    </FormLayout.Group>
                    <TextField label="Description" id="omschrijving" value={project.omschrijving || ''} multiline={7} onChange={handleChange} disabled={savingResult} />
                    <FormLayout.Group>
                      <CompanySelect isClearable id="supplierID" label="Supplier" value={project.supplierID} onChange={handleChange} isDisabled={savingResult} />
                      <TextField
                        id="aanvraag offerte"
                        maxLength={60}
                        type="date"
                        autoComplete="off"
                        label="Date request"
                        value={String(project['aanvraag offerte']).substring(0, 10)}
                        onChange={handleChange}
                        disabled={savingResult}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <TextField
                        id="onzeref"
                        maxLength={20}
                        showCharacterCount
                        autoComplete="off"
                        label="Our reference"
                        value={project.onzeref || ''}
                        onChange={handleChange}
                        disabled={savingResult}
                      />
                      <TextField
                        id="offerte"
                        maxLength={60}
                        type="date"
                        autoComplete="off"
                        label="Date offerte"
                        value={String(project.offerte).substring(0, 10)}
                        onChange={handleChange}
                        disabled={savingResult}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <TextField
                        id="refklant"
                        maxLength={100}
                        showCharacterCount
                        autoComplete="off"
                        label="Ref customer"
                        value={project.refklant || ''}
                        onChange={handleChange}
                        disabled={savingResult}
                      />
                      <TextField
                        id="Beslissing"
                        maxLength={60}
                        type="date"
                        autoComplete="off"
                        label="Date decision"
                        value={String(project.Beslissing).substring(0, 10)}
                        onChange={handleChange}
                        disabled={savingResult}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <TextField
                        id="refleverancier"
                        maxLength={20}
                        showCharacterCount
                        autoComplete="off"
                        label="Ref supplier"
                        value={project.refleverancier || ''}
                        onChange={handleChange}
                        disabled={savingResult}
                      />
                      <TextField
                        id="Leverings"
                        maxLength={60}
                        type="date"
                        autoComplete="off"
                        label="Date delivery"
                        value={String(project.Leverings).substring(0, 10)}
                        onChange={handleChange}
                        disabled={savingResult}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <TextField
                        id="Einde"
                        maxLength={60}
                        type="date"
                        autoComplete="off"
                        label="Date end"
                        value={String(project.Einde).substring(0, 10)}
                        onChange={handleChange}
                        disabled={savingResult}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <Select
                        id="status"
                        label="Status"
                        value={parseInt(project.status) || ''}
                        options={projectstatuses
                          .sort((a, b) => alphabetize(a, b, 'offertestatustekst'))
                          .map((status) => {
                            return { value: status.offertestatusnr, label: status.offertestatustekst, key: 'STATUS-' + status.offertestatusnr };
                          })}
                        onChange={(value) => {
                          handleChange(value, 'status');
                          handleStatusChange(value);
                        }}
                        disabled={savingResult}
                      />
                      <Select
                        placeholder="Select user"
                        disabled
                        id="Gebruiker"
                        label="User"
                        value={parseInt(project.Gebruiker) || ''}
                        options={users.map((user) => {
                          return { value: user.GebruikerID, label: user.Afkorting };
                        })}
                        onChange={handleChange}
                      />
                    </FormLayout.Group>
                    <TextField
                      id="statusomschrijving"
                      label="Status info"
                      value={project.statusomschrijving || ''}
                      multiline={10}
                      onChange={handleChange}
                      placeholder="Edit the project status info ✍️"
                      disabled={savingResult}
                    />
                    <TextField
                      id="verloop"
                      label="Development progress"
                      value={project.verloop || ''}
                      multiline={25}
                      onChange={handleChange}
                      placeholder="Edit the project development ✍️"
                      disabled={savingResult}
                    />
                  </FormLayout>
                </Form>
              </Card>
            </Layout.Section>
            <Layout.Section variant="oneHalf">
              <Card>
                <CardHeading title="Buy probability" />
                <Form>
                  <FormLayout>
                    <RangeSlider
                      min={0}
                      max={100}
                      prefix={'0%'}
                      suffix={'100%'}
                      step={5}
                      id="%aankoopkans"
                      label={'Buy percentage ' + (project['%aankoopkans'] ? project['%aankoopkans'] : '0') + '%'}
                      value={project['%aankoopkans'] ? project['%aankoopkans'] : '0'}
                      onChange={handleChange}
                      output
                      disabled={savingResult}
                    />
                    <RangeSlider
                      min={0}
                      max={100}
                      step={5}
                      prefix={'0%'}
                      suffix={'100%'}
                      id="%aankoopbijons"
                      label={'Buy percentage us ' + (project['%aankoopbijons'] ? project['%aankoopbijons'] : '0') + '%'}
                      value={project['%aankoopbijons'] ? project['%aankoopbijons'] : '0'}
                      onChange={handleChange}
                      output
                      disabled={savingResult}
                    />
                  </FormLayout>
                </Form>
              </Card>
              <Box paddingBlockStart={'400'}>
                <Card>
                  <CardHeading title="Docs" />
                  {id !== 'new' && project.Projectnummer ? <ProjectDropboxFolders projectNumber={project.Projectnummer} disabled={savingResult} /> : null}
                </Card>
              </Box>
              {id !== 'new' && project.ID ? (
                <Box paddingBlockStart={'400'}>
                  <InteractionsTimeline resourceType="project" resourceId={String(project.ID)} resourceName={project.Projectnummer || 'Project'} showContact={true} />
                </Box>
              ) : null}
            </Layout.Section>
          </Layout>
        </>
      )
    },
    {
      id: 'project-financial',
      content: 'Cost review',
      panelID: 'project-financial-content',
      comp: FinancialTab
    },
    {
      id: 'quotation-builder',
      content: 'Quotation Builder',
      panelID: 'quotation-builder-content',
      comp: id !== 'new' && project.ID ? <QuotationBuilderTab projectId={project.ID} project={project} /> : <Banner>Save the project first to access the Quotation Builder.</Banner>
    }
  ];

  // --- Tab selection from URL ---
  // Get tab from query param
  function getTabIndexFromUrl() {
    const params = new URLSearchParams(location.search);
    const tabId = params.get('tab');
    if (!tabId) return 0;
    const idx = tabs.findIndex((t) => t.id === tabId);
    return idx >= 0 ? idx : 0;
  }

  const [selectedTab, setSelectedTab] = useState(getTabIndexFromUrl());
  const handleTabChange = (selectedTabIndex) => {
    setSelectedTab(selectedTabIndex);
    // Update the URL with the tab param
    const params = new URLSearchParams(location.search);
    params.set('tab', tabs[selectedTabIndex].id);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  // Keep tab in sync with URL on popstate (back/forward)
  useEffect(() => {
    setSelectedTab(getTabIndexFromUrl());
    // eslint-disable-next-line
  }, [location.search]);

  // Handler for adding a one-off cost
  async function handleAddOneOffCost() {
    setAddingOneOff(true);
    // Check required fields
    if (!project.ID) {
      alert('You must save the project before adding a one-off cost.');
      setAddingOneOff(false);
      return;
    }
    if (!oneOffCost.Bedrag || !oneOffCost.FactuurTypeID) {
      alert('Please fill in all required fields.');
      setAddingOneOff(false);
      return;
    }
    // Compose payload for ProjectFactuurIn
    const payload = {
      ProjectID: project.ID,
      Bedrag: parseFloat(oneOffCost.Bedrag),
      Goedgekeurd: true,
      GoedgekeurdDoorID: user?.GebruikerID || 1,
      FactuurTypeID: oneOffCost.FactuurTypeID || (purchasetypes[0] && purchasetypes[0].BestellingTypeID),
      FactuurInID: null, // One-off, not linked to an invoice
      Notitie: oneOffCost.description
    };
    try {
      const res = await postRequest('/api/invoice-approvals', payload);
      if (res && res.Bedrag) {
        setInvoicesIn((prev) => [...prev, res]);
        setOneOffModalOpen(false);
        setOneOffCost({ Bedrag: '', description: '', FactuurTypeID: '' });
      }
    } catch (e) {
      alert('Failed to add one-off cost');
    }
    setAddingOneOff(false);
  }

  return (
    <Page pagination={pagination} title={pageTitle} subtitle={company && company.NAAM ? company.NAAM : ''} fullWidth>
      <TabTitle title={pageTitle} />
      <TheSaveBar isDirty={isDirty} handleSave={handleSave} handleDiscard={handleDiscard} savingResult={savingResult} />
      <Toasts toast={toast} setToast={setToast} errorToast={toastError} setErrorToast={setToastError} toastContent={'Project saved'} />
      <Card>
        <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
          {tabs[selectedTab].comp}
        </Tabs>
      </Card>
    </Page>
  );
}
